import { createClient } from '@supabase/supabase-js';

const supabaseUrl = process.env.REACT_APP_SUPABASE_URL;
const supabaseAnonKey = process.env.REACT_APP_SUPABASE_ANON_KEY;

// Sans ces deux variables, createClient jette de toute facon, mais avec un
// "supabaseUrl is required" nu, lance a l'import, qui laisse un ecran blanc et
// n'indique pas quoi corriger. On echoue donc ici, avec le message qui sert.
if (!supabaseUrl || !supabaseAnonKey) {
  const missing = [
    !supabaseUrl && 'REACT_APP_SUPABASE_URL',
    !supabaseAnonKey && 'REACT_APP_SUPABASE_ANON_KEY',
  ]
    .filter(Boolean)
    .join(' et ');
  throw new Error(
    `[supabase] ${missing} manquant. En local : "supabase start" puis renseigner .env ` +
      '(voir .env.example). En build de production : definir la variable avant "npm run build", ' +
      "sinon l'app est livree sans backend."
  );
}

export const supabase = createClient(supabaseUrl, supabaseAnonKey, {
  auth: {
    persistSession: true,
    autoRefreshToken: true,
    // detectSessionInUrl a false : les retours OAuth sont echanges a la main
    // (useOAuthReturn) pour ne pas entrer en conflit avec le retour Patreon (?patreon=).
    detectSessionInUrl: false,
    // pkce : le retour OAuth arrive en ?code=, echange via exchangeCodeForSession.
    // Requis pour le flow natif (le code_verifier reste dans le storage de l'app).
    flowType: 'pkce',
  },
});

// Session anonyme posee des le boot : prerequis du sync user (user_data cle par user_id).
// Idempotent : si une session existe deja (restauree du storage), on ne recree rien.
// Memoise, et c'est le coeur du sujet : la promesse est aussi le point d'attente
// de tout depart OAuth. supabase-js range le code verifier PKCE dans le meme
// storage que la session ; une session anonyme qui se pose APRES le depart vers
// Google nettoie ce storage et emporte le verifier, et au retour il n'y a plus
// rien a echanger ("PKCE code verifier not found in storage", constate le
// 29/08/2026). Attendre celle-ci avant de partir ferme la course.
let anonSession = null;

// Un aller-retour OAuth est en cours d'atterrissage : le code (ou l'erreur) est
// dans l'URL, la session va venir de l'echange, et poser un anonyme maintenant
// emporterait le code verifier PKCE range dans le meme storage.
//
// C'est la seconde moitie du meme bug. Attendre cette fonction avant de PARTIR
// (authAccount) fermait la course de l'aller ; celle du retour se joue au
// rechargement de la page de callback, ou le boot et l'echange demarrent dans la
// meme seconde. Les deux echecs de connexion Google du 29/08/2026 venaient de
// la, avec un message d'echec sur une connexion pourtant reussie cote serveur.
function oauthReturnInFlight() {
  if (typeof window === 'undefined') return false;
  try {
    const query = new URLSearchParams(window.location.search || '');
    const hash = new URLSearchParams((window.location.hash || '').replace(/^#/, ''));
    return !!(query.get('code') || hash.get('code') || query.get('error') || hash.get('error'));
  } catch {
    return false;
  }
}

// Reservee aux tests : l'etat memoise vit autrement toute la session.
export function _resetAnonSession() {
  anonSession = null;
}

export function ensureAnonSession() {
  if (!anonSession) {
    anonSession = (async () => {
      const {
        data: { session },
      } = await supabase.auth.getSession();
      if (session) return session;

      // On s'abstient, et on ne memoise pas cette abstention : un echange rate
      // laisserait sinon l'app sans session jusqu'au prochain chargement.
      if (oauthReturnInFlight()) {
        anonSession = null;
        return null;
      }

      const { data, error } = await supabase.auth.signInAnonymously();
      if (error) {
        console.error('[supabase] echec sign-in anonyme:', error.message);
        return null;
      }
      return data.session;
    })();
  }
  return anonSession;
}

// JWT de la session courante, pour authentifier les appels sync au backend
// (Authorization: Bearer). null si aucune session (le sync est alors saute).
export async function getAccessToken() {
  const {
    data: { session },
  } = await supabase.auth.getSession();
  return session ? session.access_token : null;
}
